import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import dayjs from 'dayjs'
import {
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Clock,
    XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip, Typogaphy } from 'vnetwork-ui'
import { getMailerCampaignBatches } from '../../services'
import { MailerBatch, MailerCampaign } from '../../types'

// Poll batches every 15s while campaign is actively processing.
// Replaced WebSocket-based live updates with simple React Query interval for
// simplicity — no socket connect/reconnect/auth logic to maintain.
const PROCESSING_REFETCH_INTERVAL_MS = 15_000

interface DeliveryProgressProps {
    campaign: MailerCampaign
}

const DeliveryProgress: React.FC<DeliveryProgressProps> = ({ campaign }) => {
    const { t } = useTranslation()
    const [expandedBatches, setExpandedBatches] = useState<
        Record<string, boolean>
    >({})

    const isProcessing = campaign.mailer_campaign_status === 'processing'

    const { data: batches = [], renderState: renderStateBatches } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.MAILER_CAMPAIGNS_DETAIL,
                campaign.mailer_campaign_id,
                'batches',
            ],
            queryFn: () =>
                getMailerCampaignBatches(campaign.mailer_campaign_id),
            // Auto-refresh while processing; stop polling when completed/cancelled
            refetchInterval: isProcessing
                ? PROCESSING_REFETCH_INTERVAL_MS
                : false,
        })

    const toggleBatch = (batchId: string) => {
        setExpandedBatches((prev) => ({
            ...prev,
            [batchId]: !prev[batchId],
        }))
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'sent':
                return <CheckCircle2 size={14} className="text-green-600" />
            case 'failed':
                return <XCircle size={14} className="text-red-600" />
            case 'pending':
            default:
                return <Clock size={14} className="text-yellow-600" />
        }
    }

    const getStatusColor = (
        status: string
    ): 'success' | 'error' | 'warning' => {
        switch (status) {
            case 'sent':
                return 'success'
            case 'failed':
                return 'error'
            case 'pending':
            default:
                return 'warning'
        }
    }

    const stateComponent = renderStateBatches()
    if (stateComponent) {
        return stateComponent
    }

    if (batches.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                {t('mailerCampaign:no_delivery_batches')}
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {/* Header — polling indicator shown while processing */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Typogaphy className="font-medium text-gray-800">
                        {t('mailerCampaign:delivery_batches')} ({batches.length}
                        )
                    </Typogaphy>
                    {isProcessing && (
                        <span
                            className="flex items-center gap-1 text-xs text-gray-500"
                            title={t('mailerCampaign:auto_refresh_every_15s')}
                        >
                            <Clock size={12} className="animate-pulse" />
                            <span className="hidden sm:inline">
                                {t('mailerCampaign:auto_refresh_every_15s')}
                            </span>
                        </span>
                    )}
                </div>
            </div>

            {/* Batches List */}
            {batches.map((batch: MailerBatch, index: number) => {
                // First batch expanded by default, others check state
                const isExpanded =
                    expandedBatches[batch.batch_id] ?? index === 0
                const progress =
                    batch.stats.total > 0
                        ? Math.round(
                              (batch.stats.sent / batch.stats.total) * 100
                          )
                        : 0

                return (
                    <div
                        key={batch.batch_id}
                        className="border rounded-lg overflow-hidden"
                    >
                        {/* Batch Header */}
                        <div
                            className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => toggleBatch(batch.batch_id)}
                        >
                            <div className="flex items-center gap-4">
                                <div>
                                    <Typogaphy className="font-medium text-gray-800 text-sm">
                                        {t('mailerCampaign:batch')} #
                                        {batches.length - index}
                                    </Typogaphy>
                                    <span className="text-xs text-gray-500">
                                        {dayjs(batch.started_at).format(
                                            'DD/MM/YYYY HH:mm'
                                        )}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Chip
                                        label={`${batch.stats.sent}/${batch.stats.total}`}
                                        color={
                                            progress === 100
                                                ? 'success'
                                                : batch.stats.failed > 0
                                                  ? 'warning'
                                                  : 'info'
                                        }
                                        size="small"
                                    />
                                    <span className="text-sm text-gray-500">
                                        ({progress}%)
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {/* Stats badges */}
                                <div className="flex items-center gap-2 text-xs">
                                    {batch.stats.sent > 0 && (
                                        <span className="flex items-center gap-1 text-green-600">
                                            <CheckCircle2 size={12} />{' '}
                                            {batch.stats.sent}
                                        </span>
                                    )}
                                    {batch.stats.pending > 0 && (
                                        <span className="flex items-center gap-1 text-yellow-600">
                                            <Clock size={12} />{' '}
                                            {batch.stats.pending}
                                        </span>
                                    )}
                                    {batch.stats.failed > 0 && (
                                        <span className="flex items-center gap-1 text-red-600">
                                            <XCircle size={12} />{' '}
                                            {batch.stats.failed}
                                        </span>
                                    )}
                                </div>
                                {isExpanded ? (
                                    <ChevronUp size={20} />
                                ) : (
                                    <ChevronDown size={20} />
                                )}
                            </div>
                        </div>

                        {/* Progress bar */}
                        <div className="px-4 py-2 bg-white border-t">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                    className={`h-1.5 rounded-full transition-all duration-500 ${
                                        progress === 100
                                            ? 'bg-green-500'
                                            : batch.stats.failed > 0
                                              ? 'bg-yellow-500'
                                              : 'bg-blue-500'
                                    }`}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>

                        {/* Mailers Table */}
                        {isExpanded &&
                            batch.mailers &&
                            batch.mailers.length > 0 && (
                                <div className="max-h-[320px] overflow-auto border-t">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 w-10">
                                                    #
                                                </th>
                                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                                                    Email
                                                </th>
                                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                                                    {t('name')}
                                                </th>
                                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                                                    {t('status')}
                                                </th>
                                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                                                    {t(
                                                        'mailerCampaign:sent_at'
                                                    )}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {batch.mailers.map(
                                                (mailer, idx) => (
                                                    <tr
                                                        key={mailer.mailer_id}
                                                        className={
                                                            idx % 2 === 0
                                                                ? 'bg-white'
                                                                : 'bg-gray-50'
                                                        }
                                                    >
                                                        <td className="px-4 py-2 text-xs text-gray-500">
                                                            {idx + 1}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-900">
                                                            {mailer.mailer_receiver.join(
                                                                ', '
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-600">
                                                            {mailer.metadata
                                                                ?.receiver_data
                                                                ?.ten_nguoi_nhan ||
                                                                mailer.metadata
                                                                    ?.customer_name ||
                                                                '-'}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <div className="flex items-center gap-1.5">
                                                                {getStatusIcon(
                                                                    mailer.mailer_status
                                                                )}
                                                                <Chip
                                                                    label={t(
                                                                        mailer.mailer_status
                                                                    )}
                                                                    color={getStatusColor(
                                                                        mailer.mailer_status
                                                                    )}
                                                                    size="small"
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-500">
                                                            {mailer.mailer_performed_at
                                                                ? dayjs(
                                                                      mailer.mailer_performed_at
                                                                  ).format(
                                                                      'HH:mm:ss'
                                                                  )
                                                                : '-'}
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                    </div>
                )
            })}
        </div>
    )
}

export default DeliveryProgress
